import type { AdminPayoutDetailDto, AdminPayoutPaymentDto } from '@carcheck/shared';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem, SectionTitle } from './InfoBlocks';
import { PayoutRailBadge } from './PayoutRailBadge';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatCurrency, formatDateTime } from '../../../utils/format';

/**
 * Обзор tab of the payout detail drawer (AUT-128).
 *
 * Built to the WIRE CONTRACT, NOT the mockup's older field set (Igor
 * locked decision): the mockup's «Попыток N/3», «Связанный заказ»,
 * «Последняя попытка» and «Расчёт суммы» breakdown are DROPPED — the
 * shipped `AdminPayoutDetailDto` does not carry them.
 *
 * Renders (in mockup-aligned visual order):
 *   1. Error banner (when `failed` — uses `failedReason`).
 *   2. Детали выплаты grid — inspector identity (PII-gated) + phone/email,
 *      amount/currency, rail badge, failedReason, providerPayoutId,
 *      recipient card, scheduledFor, processedAt, redirectUrl (only when
 *      present — server TTL-gates it).
 *   3. Ручное урегулирование — manual-settlement triple (only when set).
 *   4. Платёж — embedded payment summary (or empty-state).
 *   5. Цепочка повторов — supersession links (only when present).
 *
 * The payment / inspector summaries are already sanitised server-side
 * (only card last-4, never PAN / token); inspector identity is gated on
 * `view_pii` via `<RedactedField>`, agreeing with `detail.piiRevealed`.
 */
const cardLabel = (last4: string | null, brand: string | null): string => {
  if (!last4) return '—';
  return brand ? `${brand} ···· ${last4}` : `···· ${last4}`;
};

const PiiValue = ({
  value,
  revealed,
  fallback,
}: {
  value: string | null;
  revealed: boolean;
  fallback: string;
}): ReactElement => {
  if (!value) {
    return (
      <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
        {fallback}
      </Typography>
    );
  }
  return (
    <RedactedField
      perm="view_pii"
      value={value}
      redacted={value}
      {...(revealed ? { copyable: true } : {})}
    />
  );
};

const Mono = ({ children }: { children: ReactNode }): ReactElement => (
  <Typography variant="body2" fontFamily="monospace">
    {children}
  </Typography>
);

const PaymentSection = ({ payment }: { payment: AdminPayoutPaymentDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <InfoGrid>
      <InfoItem label={t('payouts.detail.payment.status')}>
        <Chip
          size="small"
          label={t(`payouts.detail.payment.statusValue.${payment.status}`, {
            defaultValue: payment.status,
          })}
        />
      </InfoItem>
      <InfoItem label={t('payouts.detail.payment.amount')}>
        {formatCurrency(Number(payment.amount), payment.currency)}
      </InfoItem>
      <InfoItem label={t('payouts.detail.payment.card')}>
        {cardLabel(payment.cardLast4, payment.cardBrand)}
      </InfoItem>
      <InfoItem label={t('payouts.detail.payment.capturedAt')}>
        {formatDateTime(payment.capturedAt)}
      </InfoItem>
    </InfoGrid>
  );
};

/** Детали выплаты — the primary field grid (locked wire field set). */
const PayoutDetailsGrid = ({ detail }: { detail: AdminPayoutDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const noValue = t('payouts.noValue');
  return (
    <InfoGrid>
      <InfoItem label={t('payouts.detail.fields.inspector')}>
        <PiiValue value={detail.inspector.name} revealed={detail.piiRevealed} fallback={noValue} />
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.amount')}>
        {formatCurrency(Number(detail.amount), detail.currency)}
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.phone')}>
        <PiiValue value={detail.inspector.phone} revealed={detail.piiRevealed} fallback={noValue} />
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.email')}>
        <PiiValue value={detail.inspector.email} revealed={detail.piiRevealed} fallback={noValue} />
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.rail')}>
        <PayoutRailBadge rail={detail.rail} />
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.failedReason')}>
        {detail.failedReason ? (
          <Typography variant="body2" color="error.main">
            {detail.failedReason}
          </Typography>
        ) : (
          noValue
        )}
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.providerPayoutId')}>
        <Mono>{detail.providerPayoutId ?? noValue}</Mono>
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.recipientCard')}>
        {cardLabel(detail.recipientCardLast4, detail.recipientCardBrand)}
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.scheduledFor')}>
        {formatDateTime(detail.scheduledFor)}
      </InfoItem>
      <InfoItem label={t('payouts.detail.fields.processedAt')}>
        {formatDateTime(detail.processedAt)}
      </InfoItem>
      {detail.redirectUrl ? (
        <InfoItem label={t('payouts.detail.fields.redirectUrl')} span>
          <Link href={detail.redirectUrl} target="_blank" rel="noopener" variant="body2">
            {t('payouts.detail.fields.redirectUrlOpen')}
          </Link>
        </InfoItem>
      ) : null}
    </InfoGrid>
  );
};

/** Ручное урегулирование — only rendered when the manual triple is set. */
const ManualSettlementSection = ({ detail }: { detail: AdminPayoutDetailDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <SectionTitle>{t('payouts.detail.sections.manual')}</SectionTitle>
      <InfoGrid>
        <InfoItem label={t('payouts.detail.fields.manualSettlementAt')}>
          {formatDateTime(detail.manualSettlementAt)}
        </InfoItem>
        <InfoItem label={t('payouts.detail.fields.manualSettlementBy')}>
          <Mono>{detail.manualSettlementBy ?? t('payouts.noValue')}</Mono>
        </InfoItem>
        {detail.manualSettlementNote ? (
          <InfoItem label={t('payouts.detail.fields.manualSettlementNote')} span>
            {detail.manualSettlementNote}
          </InfoItem>
        ) : null}
      </InfoGrid>
    </Box>
  );
};

/** Цепочка повторов — only rendered when a supersession link is present. */
const ChainSection = ({ detail }: { detail: AdminPayoutDetailDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <SectionTitle>{t('payouts.detail.sections.chain')}</SectionTitle>
      <InfoGrid>
        {detail.supersedesPayoutId ? (
          <InfoItem label={t('payouts.detail.fields.supersedesPayoutId')}>
            <Mono>{detail.supersedesPayoutId}</Mono>
          </InfoItem>
        ) : null}
        {detail.supersededById ? (
          <InfoItem label={t('payouts.detail.fields.supersededById')}>
            <Mono>{detail.supersededById}</Mono>
          </InfoItem>
        ) : null}
      </InfoGrid>
    </Box>
  );
};

export const PayoutOverviewTab = ({ detail }: { detail: AdminPayoutDetailDto }): ReactElement => {
  const { t } = useTranslation();
  // Section-visibility ONLY (does the manual-settlement block render?) — the
  // detail DTO has no `manuallySettled` boolean, unlike the list DTO. This is
  // NOT a client re-derivation of an action-eligibility predicate; the
  // Retry / Mark-manual buttons gate solely on the server-composed flags.
  const manuallySettled = detail.manualSettlementAt !== null;
  const hasChain = detail.supersededById !== null || detail.supersedesPayoutId !== null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {detail.status === 'failed' && detail.failedReason ? (
        <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
          {t('payouts.detail.errorBanner', { reason: detail.failedReason })}
        </Alert>
      ) : null}

      <Box>
        <SectionTitle>{t('payouts.detail.sections.payout')}</SectionTitle>
        <PayoutDetailsGrid detail={detail} />
      </Box>

      {manuallySettled ? <ManualSettlementSection detail={detail} /> : null}

      <Box>
        <SectionTitle>{t('payouts.detail.sections.payment')}</SectionTitle>
        {detail.payment ? (
          <PaymentSection payment={detail.payment} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('payouts.detail.payment.empty')}
          </Typography>
        )}
      </Box>

      {hasChain ? <ChainSection detail={detail} /> : null}
    </Box>
  );
};
